import {useEffect, useState} from 'react';
import {getAllProducts, insertInventory} from "../../api/Api";
import {BULK_COST_PRECISION, CategoryType} from "../../api/Constants";
import {BulkSelectWidget} from "./BulkSelectWidget";
import {Button, Checkbox, Input, InputNumber, Select, Table, Row, Col, Popover, message, Spin} from 'antd';
import {UnitType, BulkSizeType, getOptions} from "../../api/Constants";
import {BulkTotalWidget} from "./BulkTotalWidget";
import {TrashWidget} from "../widget/TrashWidget";
import {BatchSelectWidget} from "./BatchSelectWidget";
import {displayToFixedUnit, fixedToDisplayUnit} from "../../util/unitutils";

export const BulkInventoryInput = ({refresh, onRefresh, onApiFail}) => {

    const [selectedBatch, setSelectedBatch] = useState(null);
    const [bulkProducts, setBulkProducts] = useState([]);
    const [inventory, setInventory] = useState([]);

    const [searchVal, setSearchVal] = useState(undefined);
    const [product, setProduct] = useState(undefined);

    // in this form we'll treat quantity as the fixed integer value
    // we'll take input in 0.1 g and convert it to the fixed value by multiplying by 10
    const [quantity, setQuantity] = useState(0);

    const [cost, setCost] = useState(0);
    const [sku, setSku] = useState(undefined)
    const [bulkSize, setBulkSize] = useState(BulkSizeType.Bigs);
    const [book, setBook] = useState(false);

    const [messageApi, contextHolder] = message.useMessage();
    const [spin, setSpin] = useState(false);

    useEffect(() => {
        getAllProducts().then((x) => {
            const tableData = x.map((val, index) => {
                return {...val, key: val.id}
            }).filter(v => {
                return v.category === CategoryType.Wholesale;
            });
            setBulkProducts(tableData);
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [refresh, onApiFail]);

    const onDelete = (index) => () => {
        let nextInventory = [];
        for (let i=0; i<inventory.length; i++) {
            if (i !== index) {
                nextInventory.push(inventory[i]);
            }
        }
        setInventory(nextInventory);
    }

    const getTotalCost = (quantity, cost) => {
        const result = parseFloat((quantity * cost).toFixed(2));
        return result;
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (val, record, index) => {
                const displayQuantity = fixedToDisplayUnit(val, record.unit);
                return `${displayQuantity} grams`
            }
        },
        {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            render: (val) => `$ ${val} / g`
        },
        {
            title: 'Total Cost',
            dataIndex: 'cost',
            key: 'cost',
            render: (val, record) => {
                const totalCost = getTotalCost(record.quantity, record.cost);
                return totalCost;
            }
        },
        {
            title: 'Sku',
            dataIndex: 'sku',
            key: 'sku'
        },
        {
            title: 'Delete',
            dataIndex: 'name',
            key: 'delete',
            render: (val, record, index) => {
                return <TrashWidget onDelete={onDelete(index)} />
            }
        }
    ];

    const unit = UnitType.Gram;

    const bulkSizeOptions = getOptions(BulkSizeType) ();

    const onQuantityChange = (val) => {
        setQuantity(val);
    }

    const onCostChange = (val) => {
        setCost(val);
    }

    const onSkuChange = (e) => {
        setSku(e.target.value);
    }

    // automatically generate bulk skus according to
    // item sku - batch - big/small
    const generateSku = (productSku, batchName, bulkSize) => {
        const result = `${productSku}-${batchName}-${BulkSizeType.Label[bulkSize]}`
        return result.replaceAll(" ", "-");
    }

    const onBulkSizeChange = (val) => {
        setBulkSize(val);
        if (product) {
            setSku(generateSku(product.sku, selectedBatch.name, val));
        }
    }

    const onBookChange = (e) => {
        setBook(e.target.checked);
    }

    const onSelect = (product) => {
        console.log(`setting product ${JSON.stringify(product)}`);
        setProduct(product);
        const sku = generateSku(product.name, selectedBatch.name, bulkSize);
        setSku(sku);
    }

    const clearLine = () => {
        console.log(`clear line`)
        setQuantity(0);
        setCost(0);
        setSearchVal(undefined);
        setProduct(undefined);
        setSku(undefined);
        setBulkSize(BulkSizeType.Bigs);
        setBook(false);
    }

    const onSubmitClick = () => {
        if (product) {
            const newInventory = [...inventory];
            const index = newInventory.length;
            console.log(product)
            // we convert quantity from display (float) to fixed here
            newInventory.push({
                ...product,
                key: `${product.id}${index}`,
                quantity: displayToFixedUnit(quantity, unit),
                unit,
                cost,
                sku,
                bulkSize,
                itemId: product.id,
                batchId: selectedBatch.id,
                book
            });
            setInventory(newInventory);
            clearLine();
        }
    }

    const resetForm = () => {
        console.log(`clear form`);
        setSelectedBatch(null);
        setInventory([]);
        clearLine();
    }

    const onSaveInventory = async () => {
        setSpin(true);
        try {
            const promises = [];
            for (let i = 0; i < inventory.length; i++) {
                const {quantity, itemId, category, sku, unit, cost, shipping, tariff, batchId} = inventory[i];
                const totalCost = getTotalCost(quantity, cost);
                console.log(`saving inventory ${product}`);
                promises.push(insertInventory(itemId, quantity, category, {
                    sku, unit, cost: totalCost, shipping, tariff, batchId, bulkSize, book, bulkCost: cost
                }));
            }
            await Promise.all(promises).then(r => {
                messageApi.open({type: 'success', content: 'Inventory Saved'});
            })
        } catch (e) {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            console.log(`${e}`)
            messageApi.open({type: 'error', content: 'Failed to save inventory'})
        }
        if (onRefresh) { onRefresh() }
        setSpin(false);
        resetForm();
    }

    const onBatchSelect = (val) => {
        setSelectedBatch(val);
    }

    const costPrecision = BULK_COST_PRECISION;
    const tableContent = inventory.length > 0 ? <Table dataSource={inventory} columns={columns} pagination={false}/> : <div/>

    const submitDisabled = (selectedBatch === null) || (quantity === 0) || (quantity === null) || (quantity === undefined)
        || (cost === 0) || (cost === undefined) || (cost === null) || (product === undefined)
    const saveInventoryDisabled = inventory.length <= 0
    if (spin) {
        return <Spin />
    }
    return <div>
        {contextHolder}
        <Row style={{paddingTop: '5px', paddingBottom: '5px', textAlign: 'left'}}>
            <BatchSelectWidget selectedBatch={selectedBatch} onSelect={onBatchSelect} refresh={refresh} disabled={selectedBatch !== null} onApiFail={onApiFail} />
        </Row>
        {tableContent}
        <Row style={{paddingTop: '5px', paddingBottom: '5px', textAlign: 'left'}}>
            <Col span={6}><BulkSelectWidget bulkProducts={bulkProducts} searchVal={searchVal} setSearchVal={setSearchVal} onSelect={onSelect} disabled={selectedBatch === null}/></Col>
            <Col span={6}><InputNumber precision={0}  min={0} placeholder={'quantity'} value={quantity} onChange={onQuantityChange} addonAfter={"grams"}/></Col>
            <Col span={4}><Select style={{width:100}} options={bulkSizeOptions} value={bulkSize} onChange={onBulkSizeChange} popupMatchSelectWidth={false} /></Col>
        </Row>
        <Row style={{paddingTop: '5px', paddingBottom: '5px', textAlign: 'left'}}>
            <Col span={6}><Input style={{width:200}}value={sku} onChange={onSkuChange} placeholder={'sku'}></Input></Col>
            <Col span={6}><InputNumber style={{width:200}} value={cost} onChange={onCostChange} min={0} precision={costPrecision} addonBefore={'Cost/   g $ '}/></Col>
            <Col span={4} style={{paddingTop: '5px'}}><Checkbox checked={book} onChange={onBookChange}>Book</Checkbox></Col>
            <Col span={6}><Button onClick={onSubmitClick} disabled={submitDisabled}>Submit</Button></Col>
        </Row>
        <BulkTotalWidget inventory={inventory} />
        <Row style={{paddingTop: '5px', paddingBottom: '5px', textAlign: 'left'}}>
            {/*<Col span={4}><Popover content={'Reset and clear the form'}><Button onClick={resetForm}>Clear Form</Button></Popover></Col>*/}
            <Col span={4}><Popover content={'Save new products to inventory'}><Button onClick={onSaveInventory} disabled={saveInventoryDisabled}>Save Inventory</Button></Popover></Col>
            <Col style={{color: 'red', marginTop: '5px'}}>{(inventory.length > 0) && `${inventory.length} items unsaved.`}</Col>
        </Row>
    </div>
}